/**
 * Device registration for wake-and-sync (foreground, Tauri only). The native FCM service obtains a
 * registration token and emits it to the WebView as a `lapo://fcm-token` Tauri event; we forward it
 * to `POST /devices` so the sync server can push this device. A stable per-install `deviceId` (kept
 * in localStorage) makes the registration idempotent — a refreshed token overwrites the row instead
 * of piling up duplicates. On the web this is a no-op (browsers get pushes differently, out of scope).
 */
import { isTauri, isAndroid, isIOS } from './shell/shell.js';

const DEVICE_ID_KEY = 'lapo-device-id';

/** Stable per-install id; created once and reused so token refreshes replace, not duplicate. */
export function getOrCreateDeviceId(storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage): string {
  const existing = storage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const id =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `dev-${Math.abs(hashString(String(Date.now())))}`;
  storage.setItem(DEVICE_ID_KEY, id);
  return id;
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return h;
}

export interface DeviceRegistrar {
  registerDevice(deviceId: string, token: string, platform?: string): Promise<void>;
  unregisterDevice(deviceId: string): Promise<void>;
}

/**
 * Subscribe to native FCM-token events and register them. Returns an unlisten function. No-op off
 * Tauri. Safe to call repeatedly; the server upserts by (userId, deviceId).
 */
export async function initDeviceRegistration(
  api: DeviceRegistrar,
  onError: (err: unknown) => void = () => {},
): Promise<() => void> {
  if (!isTauri()) return () => {};
  const deviceId = getOrCreateDeviceId();
  // PROD-002 — tag the real platform (was hardcoded 'android') so the server can target correctly.
  // Native FCM tokens only arrive on Android today; iOS/macOS Tauri stay on focus-refetch.
  const platform = isAndroid() ? 'android' : isIOS() ? 'ios' : 'web';
  try {
    const { listen } = await import('@tauri-apps/api/event');
    const unlisten = await listen<string>('lapo://fcm-token', (event) => {
      void api.registerDevice(deviceId, event.payload, platform).catch(onError);
    });
    // Ask native to (re)emit the current token now that the WebView is listening.
    try {
      const { emit } = await import('@tauri-apps/api/event');
      await emit('lapo://request-fcm-token');
    } catch {
      /* emit is best-effort; onNewToken will still fire on refresh */
    }
    return unlisten;
  } catch (err) {
    onError(err);
    return () => {};
  }
}

/** Drop this device's token on sign-out (best-effort). */
export async function unregisterThisDevice(api: DeviceRegistrar): Promise<void> {
  if (!isTauri()) return;
  try {
    await api.unregisterDevice(getOrCreateDeviceId());
  } catch {
    /* best-effort */
  }
}
